import React, {useState, useEffect} from 'react';
import Grid from "@material-ui/core/Grid";
import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";
import makeStyles from "@material-ui/core/styles/makeStyles";
import {Button, Typography} from "@material-ui/core";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import LoaderWithBackDrop from "../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";

import { useFormik } from 'formik';
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import ProductAccessOverridesPicker from './ProductAccessOverridesPicker.comp';
import PacProductsPicker from './PacProductsPicker.comp';
import PacFeaturesPicker from './PacFeaturesPicker.comp';


const useStyles = makeStyles((theme)=>({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    modalPaper: {
        backgroundColor: theme.palette.background.paper,
        border: '2px solid #000',
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 3),
        width: '90%',
        height: '85%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    modalBody: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        paddingRight: theme.spacing(0.5),
    },
    form: {
        width: '100%',
        marginTop: theme.spacing(1),
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
    },
    tabsRoot: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        marginBottom: theme.spacing(1),
        flexShrink: 0,
    },
    tabError: {
        color: theme.palette.error.main,
    },
    sectionIntro: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
        marginBottom: theme.spacing(2),
        lineHeight: 1.5,
    },
    groupHeading: {
        fontWeight: 600,
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(0.5),
        color: theme.palette.text.primary,
    },
    footerBar: {
        flexShrink: 0,
        borderTop: `1px solid ${theme.palette.divider}`,
        paddingTop: theme.spacing(2),
        marginTop: theme.spacing(2),
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(1.5),
        backgroundColor: theme.palette.background.paper,
    },
    footerHint: {
        flex: 1,
        alignSelf: 'center',
        color: theme.palette.text.secondary,
        fontSize: '0.8rem',
    },
}));

function EditEntityModal(props) {

    const { open, columns, formValues, title, handleClose, updateAPI, fieldConfig = {}, modalConfig = {} } = props

    const classes = useStyles();

    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    const stickyFooter = modalConfig.stickyFooter === true;
    const saveLabel = modalConfig.saveLabel || 'Save';
    const cancelLabel = modalConfig.cancelLabel || 'Cancel';
    const tabLabels = modalConfig.sectionTabLabels || { display: 'Display Fields', internal: 'Functional Fields' };
    const sectionIntros = modalConfig.sectionIntros || {};

    useEffect(() => {
        if (open) setActiveTab(0);
    }, [open]);

    const hasSections = columns.some(col => col.section);
    const displayCols = hasSections ? columns.filter(c => c.section === 'display') : [];
    const internalCols = hasSections ? columns.filter(c => c.section === 'internal') : [];
    const activeCols = hasSections ? (activeTab === 0 ? displayCols : internalCols) : columns;
    const activeIntro = hasSections
        ? (activeTab === 0 ? sectionIntros.display : sectionIntros.internal)
        : null;

    const formik = useFormik({
        initialValues: formValues,
        enableReinitialize: true,
        validate: (values) => {
            const errors = {};
            for (const col of columns) {
                const cfg = fieldConfig[col.json_key] || {};
                const val = values[col.json_key];

                const isEmpty = val === undefined || val === null
                    || (typeof val === 'string' && val.trim() === '');
                if (cfg.required && isEmpty) {
                    errors[col.json_key] = `${col.name} is required`;
                    continue;
                }

                if (typeof cfg.validate === 'function') {
                    const msg = cfg.validate(val, values);
                    if (msg) errors[col.json_key] = msg;
                }
            }
            return errors;
        },
        onSubmit: async (values) => {
            await updateAPI(values);
        }
    });

    const renderField = (col) => {
        const cfg = fieldConfig[col.json_key] || {};
        if (cfg.fieldType === 'product_access_picker') {
            return (
                <ProductAccessOverridesPicker
                    value={formik.values[col.json_key]}
                    onChange={(next) => formik.setFieldValue(col.json_key, next)}
                    disabled={loading}
                    error={formik.touched[col.json_key] && formik.errors[col.json_key]}
                    helperText={cfg.helperText}
                />
            );
        }
        if (cfg.fieldType === 'pac_products_picker') {
            return (
                <PacProductsPicker
                    value={formik.values[col.json_key]}
                    onChange={(next) => formik.setFieldValue(col.json_key, next)}
                    disabled={loading}
                    helperText={cfg.helperText}
                />
            );
        }
        if (cfg.fieldType === 'pac_features_picker') {
            return (
                <PacFeaturesPicker
                    value={formik.values[col.json_key]}
                    onChange={(next) => formik.setFieldValue(col.json_key, next)}
                    disabled={loading}
                    helperText={cfg.helperText}
                />
            );
        }
        const selectOptions = cfg.selectOptions || null;
        const isSelect = Array.isArray(selectOptions) && selectOptions.length > 0;
        const isMultiline = cfg.multiline !== undefined ? cfg.multiline : true;
        const rowsMax = cfg.rowsMax || 6;
        const fieldType = cfg.type || 'text';
        return (
            <TextField
                variant="outlined"
                required={cfg.required !== undefined ? cfg.required : true}
                fullWidth
                multiline={isSelect ? false : isMultiline}
                rowsMax={isSelect ? undefined : rowsMax}
                id={col.json_key}
                label={col.name}
                name={col.json_key}
                type={fieldType}
                select={isSelect}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values[col.json_key] !== undefined ? formik.values[col.json_key] : ''}
                error={Boolean(formik.touched[col.json_key] && formik.errors[col.json_key])}
                helperText={
                    (formik.touched[col.json_key] && formik.errors[col.json_key])
                        ? formik.errors[col.json_key]
                        : (cfg.helperText || '')
                }
                inputProps={cfg.inputProps || undefined}
            >
                {isSelect && selectOptions.map((opt) => (
                    <MenuItem key={String(opt.value)} value={opt.value}>
                        {opt.label}
                    </MenuItem>
                ))}
            </TextField>
        );
    };

    const renderSaveFooter = () => (
        <div className={classes.footerBar}>
            {stickyFooter && (
                <Typography className={classes.footerHint}>
                    Saves all fields on both tabs — you do not need to switch tabs first.
                </Typography>
            )}
            <Button variant="outlined" onClick={handleClose} disabled={loading}>
                {cancelLabel}
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={loading}>
                {saveLabel}
            </Button>
        </div>
    );

    return (
        <>
            <LoaderWithBackDrop loading={loading} />
            <Modal
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                className={classes.modal}
                open={open}
                onClose={handleClose}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{ timeout: 500 }}
            >
                <Fade in={open}>
                    <div
                        className={classes.modalPaper}
                        style={{
                            width: modalConfig.width || '90%',
                            height: modalConfig.height || '85%',
                            maxWidth: modalConfig.maxWidth || undefined,
                        }}
                    >
                        <Typography variant="h6" id="transition-modal-title" style={{ flexShrink: 0 }}>
                            {title}
                        </Typography>

                        {hasSections && (() => {
                            const displayHasErrors = Object.keys(formik.errors).some(k => displayCols.some(c => c.json_key === k));
                            const internalHasErrors = Object.keys(formik.errors).some(k => internalCols.some(c => c.json_key === k));
                            const showErr = formik.submitCount > 0;
                            return (
                                <Tabs
                                    value={activeTab}
                                    onChange={(_, v) => setActiveTab(v)}
                                    indicatorColor="primary"
                                    textColor="primary"
                                    className={classes.tabsRoot}
                                >
                                    <Tab label={
                                        <span className={showErr && displayHasErrors ? classes.tabError : undefined}>
                                            {tabLabels.display}
                                        </span>
                                    } />
                                    <Tab label={
                                        <span className={showErr && internalHasErrors ? classes.tabError : undefined}>
                                            {tabLabels.internal}
                                        </span>
                                    } />
                                </Tabs>
                            );
                        })()}

                        <form className={classes.form} noValidate onSubmit={formik.handleSubmit}>
                            <div className={classes.modalBody}>
                                {activeIntro && (
                                    <Typography className={classes.sectionIntro}>{activeIntro}</Typography>
                                )}
                                <Grid container spacing={2}>
                                    {activeCols.map(col => (
                                        <React.Fragment key={col.json_key}>
                                            {col.groupLabel && (
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2" className={classes.groupHeading}>
                                                        {col.groupLabel}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            <Grid item xs={12}>
                                                {renderField(col)}
                                            </Grid>
                                        </React.Fragment>
                                    ))}
                                </Grid>
                            </div>

                            {stickyFooter ? renderSaveFooter() : (
                                <Grid container spacing={2}>
                                    <Grid item xs={4} />
                                    <Grid item xs={4}>
                                        <Button
                                            type="submit"
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            style={{ marginTop: 16 }}
                                        >
                                            {saveLabel}
                                        </Button>
                                    </Grid>
                                </Grid>
                            )}
                        </form>
                    </div>
                </Fade>
            </Modal>
        </>
    );
}

export default EditEntityModal;
